import React, { useState } from 'react';
import { Html } from '@react-three/drei';
import { StructureShape, SPECIAL_STRUCTURES } from './structures';
import { darkenColor } from './structures/utils';

// Re-export FacilityMarker from its dedicated module so existing
// imports (`import { FacilityMarker } from './3d/StructureRenderer'`)
// keep working without touching call sites.
export { FacilityMarker } from './FacilityMarker';

/**
 * StructureRenderer — thin wrapper that positions, rotates and
 * decorates any booth. The heavy lifting (geometry per structure
 * type) lives in `/structures/*.js`.
 *
 * Responsibilities kept here:
 *  • hover / click / selection state
 *  • booth-number floating label + selection ring + shelter halo
 *  • company name popup when selected
 *  • world-space positioning & rotation from the booth record
 */
export function StructureRenderer({ booth, onSelect, isSelected, weatherCondition }) {
  const [hovered, setHovered] = useState(false);

  const isRaining = ['rainy', 'stormy'].includes(weatherCondition);
  const shelterHighlight = isRaining && booth.has_shelter;

  const baseColor = booth.status === 'available' ? '#95a5a6' : (booth.color || '#3b82f6');
  const roofColor = shelterHighlight ? '#3498db' : (booth.status === 'available' ? '#7f8c8d' : darkenColor(baseColor, 0.2));
  const emissive = hovered || isSelected ? 0.4 : (shelterHighlight ? 0.25 : 0);

  const w = booth.width || 6;
  const d = booth.depth || 6;
  const h = booth.height || 3.5;

  // Standard exhibitor booths all render as a uniform 20x20 ft canopy tent
  // (~6m × 6m × 3m). Special structures (main stage, food truck, kids
  // playground, garden plot, MyKiosk) keep the dimensions stored in the DB
  // so their signature 3D look is preserved.
  const structureType = booth.structure_type || 'khemah_pempamer';
  const isSpecial = SPECIAL_STRUCTURES.has(structureType);
  const finalW = isSpecial ? w : 6;
  const finalD = isSpecial ? d : 6;
  const finalH = isSpecial ? h : 3;

  const handleClick = (e) => { e.stopPropagation(); onSelect(booth); };
  const handleOver = (e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; };
  const handleOut = () => { setHovered(false); document.body.style.cursor = 'auto'; };

  const geom = { w: finalW, d: finalD, h: finalH, baseColor, roofColor, emissive };
  // Label/ring maths use the same final dims so the selection ring hugs the shape.
  const labelW = finalW;
  const labelD = finalD;
  const labelH = finalH;

  return (
    <group
      position={[booth.position_x, 0, booth.position_z]}
      rotation={[0, (booth.rotation || 0) * Math.PI / 180, 0]}
      onClick={handleClick}
      onPointerOver={handleOver}
      onPointerOut={handleOut}
    >
      <StructureShape structureType={structureType} {...geom} />

      {/* Booth number floating label */}
      <Html zIndexRange={[10, 0]} position={[0, labelH + 3, 0]} center distanceFactor={40} occlude={false}>
        <div
          className="pointer-events-none px-2 py-0.5 bg-white text-slate-900 font-bold text-xs rounded shadow whitespace-nowrap"
          style={{
            transform: `scale(${hovered || isSelected ? 1.3 : 1})`,
            transition: 'transform 0.2s',
            boxShadow: isSelected ? '0 0 20px rgba(243, 156, 18, 0.9)' : '',
          }}
        >
          {booth.booth_number}
        </div>
      </Html>

      {/* Selection ring */}
      {isSelected && (
        <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[Math.max(labelW, labelD) * 0.6, Math.max(labelW, labelD) * 0.65, 32]} />
          <meshBasicMaterial color="#f39c12" transparent opacity={0.7} />
        </mesh>
      )}

      {/* Shelter halo when raining */}
      {shelterHighlight && !isSelected && (
        <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[Math.max(labelW, labelD) * 0.65, Math.max(labelW, labelD) * 0.72, 32]} />
          <meshBasicMaterial color="#3498db" transparent opacity={0.4} />
        </mesh>
      )}

      {/* Company popup when selected */}
      {isSelected && booth.company && (
        <Html zIndexRange={[10, 0]} position={[0, labelH + 5, 0]} center>
          <div className="bg-slate-900/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-2xl border border-orange-500/50 whitespace-nowrap pointer-events-none">
            <div className="text-white font-bold text-xs">{booth.company.name}</div>
            <div className="text-orange-400 text-[10px]">{booth.category?.name}</div>
          </div>
        </Html>
      )}
    </group>
  );
}
